import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, newId } from '@battleops/localstore';
import { commands, rankListTemplates, listProgress, taskList } from '@battleops/shared';
import { db } from './store';
import { useLocalCollection } from './useLocal';
import { commitWrites } from './commit';
import { useTasks } from '../context/TaskContext';

// Lists on tasks (v0.1.227, shared/lists.js + shared/commands/lists.js). Phone and web share these hooks.
// Items are information, not work: every write here touches the task's `list` field (and the list-template library),
// never its status, time or completion — except "Make tasks from unticked", which is explicit.

/** The list-template library: { templates (by name), byId, ranked(departmentId, q), save, remove }. */
export function useListTemplates() {
  const rows = useLocalCollection('listTemplates');
  const templates = useMemo(() => [...rows].sort((a, b) => String(a.name).localeCompare(String(b.name))), [rows]);
  const byId = useMemo(() => new Map(templates.map((t) => [t.id, t])), [templates]);
  const ranked = useCallback((departmentId = null, q = '') => rankListTemplates(templates, { departmentId, q }), [templates]);
  const save = useCallback((tpl) => {
    const r = commands.saveListTemplate({ newId, now: Date.now(), listTemplates: templates }, tpl);
    commitWrites(r.writes).catch((e) => console.error('list template', e));
    return r.template;
  }, [templates]);
  const remove = useCallback((id) => {
    const r = commands.deleteListTemplate({ listTemplates: templates }, { id });
    commitWrites(r.writes).catch((e) => console.error('list template', e));
  }, [templates]);
  return { templates, byId, ranked, save, remove };
}

/**
 * One task's list. Each change reads the task as committed (after the change before it), so quick ticks in a row never
 * overwrite each other; the screen shows the result at once (optimistic) until the store catches up.
 */
export function useTaskList(task) {
  const { tasks } = useTasks();
  const templates = useLocalCollection('listTemplates');
  const [optimistic, setOptimistic] = useState(null);
  const queue = useRef(Promise.resolve());
  const pending = useRef(0);
  const taskId = task?.id || null;
  const stored = taskList(task);
  // The store's copy wins again once nothing of ours is still on its way.
  useEffect(() => { if (!pending.current) setOptimistic(null); }, [task?.list, taskId]);
  const list = optimistic === false ? null : optimistic || stored;

  const run = useCallback((cmd, input, { allTasks = false } = {}) => {
    if (!taskId) return Promise.resolve(null);
    pending.current += 1;
    const p = queue.current.then(async () => {
      const snap = await getDoc(doc(db, 'tasks', taskId));
      if (!snap.exists()) return null;
      const cur = { id: taskId, ...snap.data() };
      const scope = allTasks ? [...(tasks || []).filter((t) => t.id !== taskId), cur] : [cur];
      const r = commands[cmd]({ tasks: scope, listTemplates: templates, newId, now: Date.now() }, { taskId, ...input });
      if (r.list !== undefined) setOptimistic(r.list);
      else if (cmd === 'removeList') setOptimistic(false);
      await commitWrites(r.writes);
      return r;
    }).catch((e) => { console.error(`list ${cmd}`, e); return null; })
      .finally(() => { pending.current -= 1; });
    queue.current = p;
    return p;
  }, [taskId, tasks, templates]);

  return {
    list,
    progress: listProgress(list),
    add: (text, qty = null) => run('addListItem', { text, qty }),
    addMany: (items, extra = {}) => run('addList', { items, ...extra }),
    tick: (itemId, done) => run('setListItem', { itemId, done }),
    edit: (itemId, patch) => run('setListItem', { itemId, ...patch }),
    remove: (itemId) => run('removeListItem', { itemId }),
    attach: (templateId) => run('attachTemplate', { templateId }),
    saveAsTemplate: (opts = {}) => run('saveListAsTemplate', opts),
    toTasks: (as = 'subtasks') => run('listToTasks', { as }, { allTasks: true }),
    clearTicked: () => run('clearTicked', {}),
    removeList: () => run('removeList', {}),
  };
}
